/**
 * Vspot generic directives. We are including some common components that are used
 * almost everywhere in the site.
 * @module global/vspot
 */
define('vspot',['jquery',
        'jquery-ui',
        'angular',
        'moment',
        'moment-timezone',
        'angular-route',
        'angular-sanitize',
        'xdate',
        'error-handler',
        'angular-ui-bootstrap',
        'vspot-resources',
        'vspot-services',
        'localize',
        'feedback-link', // used on all the logged-in pages, except responsive and pop-up.
        'signin', // used in nav-full, which is on all the logged-in pages, except responsive and pop-up.
        'dashboard-nav' // used in nav-full, which is on all the logged-in pages, except responsive and pop-up.
        ], function($, ui, ng, moment) {
    'use strict';

    var unlimited = -1; // Should get this from Rails

    var vspot = ng.module('vspot', ['ngRoute', 'ui.bootstrap', 'error-handler', 'ngSanitize', 'localization']);

    /*
     * vspotFieldsMatch:
     *      Adds an error state for checking that one field matches another field.
     *      Set the attribute value to the ID of the field you want to match against.
     *
     * Attrs:
     *      vspot-fields-match-case-insensitive: {bool} optional. Defaults to false.
     *
     * <input type="password" name="foo" vspot-fields-match="other-id" vspot-fields-match-case-insensitive="true">
     */
    vspot.directive('vspotFieldsMatch', ['$timeout', function ($timeout) {
        return {
            require: 'ngModel',
            link: function (scope, elem, attrs, ctrl) {
                if( !global_helper.blank(elem) ) {
                    if( !global_helper.blank(attrs.vspotFieldsMatch) ) {
                        var matches;
                        var otherField = $('#' + attrs.vspotFieldsMatch);

                        // Assume it's case-sensitive
                        var caseSensitive = true;
                        if( !global_helper.blank(attrs.vspotFieldsMatchCaseInsensitive) && attrs.vspotFieldsMatchCaseInsensitive.toLowerCase() === 'true' ) {
                            caseSensitive = false;
                        }

                        // If the user leaves the other field, we want to recheck this field
                        otherField.on('blur', function(event){
                            $timeout(function() {
                                ctrl.$validate();
                            });
                        });

                        ctrl.$validators.vspotFieldsMatch = function (modelValue, viewValue) {
                            var value = modelValue || viewValue;
                            if( caseSensitive || global_helper.blank(value) || global_helper.blank(otherField.val())) {
                                // Take the fields as they are
                                matches = value === otherField.val();
                            } else {
                                // Make sure we use the lower case versions. (null handled above)
                                matches = value.toLowerCase() === otherField.val().toLowerCase();
                            }

                            return matches;
                        };
                    }
                }
            }
        };
    }]);

    vspot.directive('vspotValidateEmail', ['$compile', 'services.User', '$q', function ($compile, User, $q) {
        return {
            require: 'ngModel',
            link: function (scope, elem, attrs, ctrl) {
                if( !global_helper.blank(elem) ) {
                    elem.parent().addClass('has-feedback');

                    // checkAvailable sets "strict" checking on the server. We use "strict" checking for registration, and non-strict for changing the user's email
                    scope.checkAvailable    = attrs.vspotValidateEmail === 'true'; // Attrs are always a string. 'false' or undefined otherwise.
                    scope.emailAvailable    = null;
                    scope.checkingEmail     = false;
                    scope.errorCode         = null;
                    scope.checkedValue      = null; // Value on last check, when the error was generated
                    scope.domain            = null; // The domain the server checked... returned from the server.

                    var ngIfVal = 'errorCode'; // Default to just the scope variable
                    if( elem && elem[0] && elem[0].name && elem[0].form && elem[0].form.name && scope.$eval( elem[0].form.name ) ) {
                        // We found a form
                        ngIfVal += ' && !' + elem[0].form.name + '.' + elem[0].name + '.$error.email && !' + elem[0].form.name + '.' + elem[0].name + '.$error.required';
                    }

                    // We're not using a template because we just need to decorate the input field that's there, rather than create a new one.
                    // The form-control-feedback class positions the icons absolutly so they appear in the email input field.
                    var validationHTML = '<i class="glyphicon glyphicon-ok-circle form-control-feedback save-color ng-hide" aria-hidden="true" ng-show="emailAvailable"></i>' +
                        '<i class="glyphicon glyphicon-ban-circle form-control-feedback ng-hide cancel-color" aria-hidden="true" ng-show="emailAvailable === false"></i>' +
                        '<i class="fa fa-spinner fa-pulse loader-icon form-control-feedback ng-hide" ng-show="checkingEmail"></i>' +
                        '<label for="' + attrs.id + '" class="text-danger" ng-if="' + ngIfVal + '" data-i18n="{{errorCode}}|{{checkedValue}}|{{domain}}"></label>';

                    elem.after( $compile(validationHTML)(scope) );

                    ctrl.$asyncValidators.vspotValidateEmail = function (modelValue, viewValue) {
                        scope.checkedValue      = modelValue || viewValue;
                        scope.emailAvailable    = null;
                        scope.checkingEmail     = true;
                        scope.errorCode         = null;
                        scope.domain            = null;

                        return User.verifyEmail(scope.checkedValue, scope.checkAvailable).$promise.then(function (result) {
                            scope.checkingEmail = false;
                            var retVal          = false;

                            if( result.status ) {
                                // All good
                                scope.emailAvailable    = true;
                                retVal                  = true;
                            } else {
                                // Display an error
                                scope.emailAvailable    = false;
                                scope.domain            = result.domain;
                                scope.errorCode         = result.code;

                                if( result.overrideable ) {
                                    // User can ignore the error, set form field to a valid state
                                    retVal = true;
                                } else {
                                    // Fatal error, for field should be invalid
                                    retVal = $q.reject(false);
                                }
                            }
                            return retVal;
                        });
                    };
                }
            }
        };
    }]);

    /*
     * Adds validation to an email field to ensure user is registered.
     * For use in cases such as adding assistant organizers or teachers.
     *
     * You can stipulate a callback function as the parameter if you want one:
     * <input type="email" ... vspot-user-registered="myCallbackFunction">
     *
     * The callback function is only called on success.
     * The callback receives one parameter, an object with two properties:
     *      result: The result of the successful call to verifity the user is registered.
     *      attrs: The attributes on the input field, so you can send any additional data
     *          you want to the callback function by including it as a attribute.
     *
     * You may override the default behavior to require the user be registered for
     * the field to be in success. If so, the callback will be called either way,
     * and the field will be validated. This can be useful for making decisions
     * based on whether the user is registered, but registration isn't actually a
     * requirement.
     * <input type="email" ... vspot-user-registered="myCallbackFunction" require-registered="false">
     */
    vspot.directive('vspotUserRegistered', ['$compile', 'services.User', '$q', '$timeout', function ($compile, User, $q, $timeout) {
        return {
            require: 'ngModel',
            link: function (scope, elem, attrs, ctrl) {
                if( !global_helper.blank(elem) ) {
                    elem.parent().addClass('has-feedback');
                    scope.userRegistered    = null;
                    scope.checkingEmail     = false;
                    scope.errorCode         = null;
                    scope.theCheck          = null;

                    var callback = ng.noop;
                    var tmpCallback = scope.$eval(attrs.vspotUserRegistered);
                    if( global_helper.blank( tmpCallback ) ) {
                        // Try the parent - maybe we're in an ng-repeat
                        tmpCallback = scope.$parent.$eval(attrs.vspotUserRegistered);
                    }

                    if( !global_helper.blank( tmpCallback ) ) {
                        callback = tmpCallback;
                    }

                    var requireRegistered = true;
                    if( !global_helper.blank( attrs.requireRegistered ) && attrs.requireRegistered === 'false' ) {
                        requireRegistered = false;
                    }

                    $timeout(function() {
                        var validationLabelIf           = 'errorCode'; // Default to just the scope variable
                        var validationRegIconShow       = 'userRegistered === true';
                        var validationCancelIconShow    = 'userRegistered === false';
                        if( elem && elem[0] && elem[0].name && elem.closest("[ng-form]").attr("name") ) {
                            // We found a form
                            var fieldBaseName           = elem.closest("[ng-form]").attr("name") + '.' + elem[0].name;
                            validationLabelIf           += ' && !' + fieldBaseName + '.$error.email && !' + fieldBaseName + '.$error.required';
                            validationRegIconShow       += ' && !' + fieldBaseName + '.$error.email && !' + fieldBaseName + '.$error.required';
                            validationCancelIconShow    += ' && !' + fieldBaseName + '.$error.email && !' + fieldBaseName + '.$error.required';
                        }

                        // We're not using a template because we just need to decorate the input field that's there, rather than create a new one.
                        // The form-control-feedback class positions the icons absolutly so they appear in the email input field.
                        var validationHTML = '<i class="glyphicon glyphicon-ok-circle form-control-feedback save-color ng-hide" aria-hidden="true" ng-show="' + validationRegIconShow + '"></i>' +
                            '<i class="glyphicon glyphicon-ban-circle form-control-feedback ng-hide cancel-color" aria-hidden="true" ng-show="' + validationCancelIconShow + '"></i>' +
                            '<i class="fa fa-spinner fa-pulse loader-icon form-control-feedback ng-hide" ng-show="checkingEmail"></i>' +
                            '<label for="' + attrs.id + '" class="text-danger" ng-if="' + validationLabelIf + '" data-i18n="{{errorCode}}"></label>';

                        elem.after( $compile(validationHTML)(scope) );
                    });

                    ctrl.$asyncValidators.vspotUserRegistered = function (modelValue, viewValue) {
                        if( !scope.theCheck ) {
                            scope.checkedValue      = modelValue || viewValue;
                            scope.errorCode         = null;
                            scope.userRegistered    = null;
                            scope.checkingEmail     = true;
                            scope.theCheck          = User.verifyRegistered(scope.checkedValue).$promise.then(function (result) {
                                scope.checkingEmail = false;
                                var retVal          = false;

                                if( scope.checkedValue && requireRegistered && !result.status ) {
                                    // Display an error
                                    scope.errorCode         = result.code;
                                    retVal                  = $q.reject(false);
                                    scope.userRegistered    = false;
                                } else {
                                    // All good
                                    retVal                  = true;
                                    scope.userRegistered    = true;
                                    // Call the callback, if there is one...
                                    callback({attrs: attrs, result: result});
                                }
                                scope.theCheck = null;
                                return retVal;
                            });
                        }
                        
                        return scope.theCheck;
                    };
                }
            }
        };
    }]);


    /*
     * vspotAutofocus:
     * Focus on a form field when the page (or template if loading from one) loads.
     * NOTE: if using the vspotMarkFocus directive with vspotAutofocus, vspotAutofocus
     * must come first:
     * <input type="text" name="foo" data-vspot-autofocus data-vspot-mark-focus>
     */
    vspot.directive('vspotAutofocus', ['$timeout', function($timeout) {
        return {
            link: function(scope, element, attrs) {
                $timeout( function(){
                    if (element && typeof(element) !== 'undefined') {
                        element.focus();
                    }
                }, 0 );
            }
        };
    }]);

    /*
     * focus-on:
     * Allows an input to be automatically focused on
     * when the attribute is set to true.
     * Not for use with focusing on page load.
     * Use data-vspot-autofocus instead.
     */
    vspot.directive('focusOn', function() {
        return {
            restrict: 'A',
            link:function (scope, element, attrs) {
                scope.$watch(attrs.focusOn, function(newValue, oldValue){
                    if(newValue && newValue !== oldValue) {
                        element.focus();
                    }
                }, true);
            }
        };
    });

    /* DEPRECATED! (used in the universal form)
     * vspotEmailValid:
     * This makes a call back to the server to ask if the domain accepts email for this user.
     * Errors are stored in scope.vspot_email_error.
     * NOTE: Display Angular email errors first:
     *      data-ng-show="form.em_addr.$dirty && form.em_addr.$error.vspotEmailValid && focused!='em_addr' && !(form.em_addr.$error.required || form.em_addr.$error.email)"
     */
    vspot.directive('vspotEmailValid', ['$http', '$sce', function($http, $sce) {
        var toId;

        return {
            restrict: 'A',
            require: 'ngModel',
            link: function(scope, elem, attr, ctrl) {
                //when the scope changes, check the email.
                scope.$watch(attr.ngModel, function(value) {
                    // if there was a previous attempt, stop it.
                    if (toId) {
                        clearTimeout(toId);
                    }

                    if(typeof(value) !== 'undefined' && value.length > 0) {
                        elem.addClass('vspot-email-valid-loading');
                        scope.vspot_email_submitting = true;
                        elem.removeClass('vspot-email-valid-check');
                        ctrl.$setValidity('vspotEmailValid', true);

                        toId = setTimeout(function() {
                            $http.post(
                                '/login/send_api_check_email',
                                {
                                    email: value
                                }
                            )
                                .success(function(result) {
                                    elem.removeClass('vspot-email-valid-loading');
                                    scope.vspot_email_submitting = false;

                                    if(result.status === true) {
                                        // It worked
                                        elem.addClass('vspot-email-valid-check');
                                        ctrl.$setValidity('vspotEmailValid', true);
                                        scope.vspot_email_error = '';
                                    } else {
                                        // It's an error message
                                        scope.vspot_email_error = $sce.getTrustedHtml(result.message);

                                        ctrl.$setValidity('vspotEmailValid', false);
                                    }
                                })
                                .error(function(result){
                                    elem.removeClass('vspot-email-valid-loading');
                                    scope.vspot_email_submitting = false;
                                    elem.removeClass('vspot-email-valid-check');
                                });
                        }, 200);
                    }
                });

            }
        };
    }]);



    /*
     * vspotMarkFocus:
     * Set $scope.focused equal to the field with the current focus. This gets a little
     * hairy in child scopes like ng-repeats, and you may need to move up the scope
     * chain by a $parent or two to get to the same level as the form.
     *
     * NOTE: if using the vspotMarkFocus directive with vspotAutofocus, vspotAutofocus
     * must come first:
     * <input type="text" name="foo" vspot-autofocus vspot-mark-focus>
     *
     * @param vspot-mark-focus [optional] - if set to "parent" it uses the parent scope
     *      instead of the current scope. If set to a number, goes up the $parent chain
     *      that many times.
     *
     * NOTE: The use of vspot-show-error mimics the behavior in the vspot validation directive.
     */
    vspot.directive('vspotMarkFocus', ['$timeout', function($timeout) {
        return {
            restrict: 'A',
            link: function(scope, elem, attr) {
                var myscope = scope;
                if( !global_helper.blank(attr.vspotMarkFocus) ) {
                    // A value was passed in
                    if(attr.vspotMarkFocus === 'parent') {
                        myscope = myscope.$parent;
                    } else {
                        var numParents = parseInt( attr.vspotMarkFocus, 10 );
                        if( numParents > 0 ) {
                            do {
                                myscope = myscope.$parent;
                                numParents--;
                            } while (numParents > 0)
                        }
                    }
                }

                elem.on('focus', function(event){
                    var element = $('#' + event.target.id);
                    if( element.hasClass('vspot-show-error') ) {
                        /* skip marking the elment as focued because vspot-show-error means we want errors to show
                         * even when the user has clicked on the element. But, we want to remove the class because
                         * if the user click off and back on we want it to behave like normal.
                         */
                         if( element.val() === '' ) {
                            // We need to get the browser to see the field as dirty
                            $timeout(function() {
                                element.val(' ');
                                $timeout(function() {
                                    element.val('');
                                });
                            });
                         }
                         element.removeClass('vspot-show-error');
                    } else {
                        // Mark focus.
                        $timeout(function(){
                            myscope.focused = event.target.id;
                            element.addClass('ng-focus'); // Can remove this line when done with the older style .form-row
                        });
                    }
                });

                elem.on('blur', function(event){
                    $timeout(function(){
                        myscope.focused = null;
                        $('#' + event.target.id).removeClass('ng-focus'); // Can remove this line when done with the older style .form-row
                    });
                });
            }
        };
    }]);


    /* @deprecated
     * Use date-range component
     * vspotDateRange:
     *      Display a date range in the format xxx - yyy
     *
     *  @param data-start-date - the start date in yyyy/mm/dd format
     *  @param data-end-date - the end date in yyyy/mm/dd format
     *
     *  Output is to insert a span with the date range in the elem, or to log errors to the console.
     *
     *  Example usage:
     *      <td data-vspot-date-range data-start-date="{{d.startdate}}" data-end-date="{{d.enddate}}"></td>
     */
    vspot.directive('vspotDateRange', ['$log', '$rootScope', function($log, $rootScope) {
        return {
            restrict:   'A',
            link:       function(scope, elem, attr) {
                var no_date         = $rootScope.vspotCreateDate('2038/01/01').getTime();
                var date_val        = '';
                var date_format     = 'MMM dd, yyyy';
                var input_format    = /^\d{4}\/\d{2}\/\d{2}$/;
                var start_date      = false;
                var end_date        = false;

                if( global_helper.blank(attr.startDate) ) {
                    // Log an error and return
                    $log.error('Start date is required.');
                } else {
                    // We have a start date, but is it in the right format
                    if( attr.startDate.match(input_format) === null ) {
                        // No match
                        $log.error('Start date must be in the format yyyy/mm/dd.', attr.startDate);
                    } else {
                        // Now, can we make a date out of it?
                        start_date = $rootScope.vspotCreateDate(attr.startDate);
                        if( !start_date.valid() ) {
                            $log.error('Start date is not valid!', attr.startDate, start_date);
                        } else {
                            // Double check it's not a no-date
                            if( start_date.getTime() !== no_date ) {
                                // Update the date string
                                date_val = start_date.toString(date_format);
                                // $log.log('Start date valid!', date_val);

                                // Now check for an end date
                                if( typeof(attr.endDate) !== 'undefined' ) {
                                    // We have an end date to check
                                    if( attr.endDate.match(input_format) !== null ) {
                                        end_date = $rootScope.vspotCreateDate(attr.endDate);

                                        if( !end_date.valid() ) {
                                            $log.error('End date is not valid!', attr.endDate, end_date);
                                        } else {
                                            if( end_date.getTime() !== no_date && start_date.diffDays(end_date) > 0 ) {
                                                date_val += ' - ' + end_date.toString(date_format);
                                            }
                                        }
                                    }

                                }

                            } else {
                                // $log.log('Start date is a no-date value, skipping.', attr.startDate);
                            }
                        }
                    }

                }

                elem.append('<span>' + date_val + '</span>');
            }
        };
    }]);


    /* @deprecated
     * Use time-range component
     */
    vspot.directive('vspotTimeRange', ['$log', function($log) {
        return {
            templateUrl: '/script/vspot-angular/templates/vspot-time-range.html',
            restrict:   'A',
            link: function(scope, element, attrs) {
                var timeFormat = 'h:mmtt';
                var result = '';
                var start = scope.vspotCreateDate('1970-01-01');
                var end = start.clone();

                scope.startTime = false;
                scope.dash = false;
                scope.endTime = false;

                if(parseInt(attrs.startHour, 10) !== unlimited) {
                    // We can set the start time
                    start.setHours(attrs.startHour);
                    start.setMinutes(attrs.startMinute);

                    scope.startTime = start.toString(timeFormat);

                    if(parseInt(attrs.endHour, 10) !== unlimited) {
                        /*
                         * NOTE: The end time can be before the start time...
                         * such as 10:00pm - 1:00am. I.e. the event goes past
                         * midnight.
                         */
                        end.setHours(attrs.endHour);
                        end.setMinutes(attrs.endMinute);
                        scope.dash = true;
                        scope.endTime = end.toString(timeFormat);
                    }
                }

//                if(result.length > 0){
//                    // We can append it
//                    element.append('<span>' + result + '</span>');
//                }
            }
        };
    }]);


    /* DEPRECATED!
     * This is still in use on the /Newsletter page, but not really a good idea to use beyond that.
     * Should probably replace it on the /Newsletter page some time.
     */
    vspot.directive('vspotUniversalForm', ['$log', '$http', '$rootScope', function($log, $http, $rootScope){
        return {
            restrict:   'A',
            link: function(scope, element, attrs) {
                var form_name           = attrs.name;
                var form                = scope[form_name];
                var fields_name         = attrs.vspotUniversalForm;
                var fields              = scope[fields_name];
                var generic_error       = 'An unknown error occurred. Please reload your browser and try again, or contact <a href="mailto:support@SignUp.com" target="_top">support@SignUp.com</a> for further assistance.';
                var ty_url              = '';
                var use_ty_template     = false;

                if( typeof(attrs.vspotThankyouPage) !== 'undefined' ) {
                    ty_url = attrs.vspotThankyouPage;
                    if(ty_url.indexOf('?') === -1) {
                        ty_url += '?';
                    } else {
                        ty_url += '&amp;';
                    }

                    ty_url += 'gapromogoal=done';
                }

                if( typeof(attrs.vspotUseThankyouTemplate) !== 'undefined' ) {
                    use_ty_template = (attrs.vspotUseThankyouTemplate === 'true');
                }

                var submit_form = function() {
                    $http.post('/api/form_entries/', fields)
                        .success(function(data, status) {
                            scope.submitting = false;
                            if(data.status) {
                                // It worked... now what?
                                if( ty_url.length > 0 ) {
                                    window.location = ty_url;
                                } else if( use_ty_template ) {
                                    $log.log('Use Thankyou Template', use_ty_template);
                                    $rootScope.$broadcast('vspotUniversalForm.ThankyouTemplate');
                                } else if (data.message) {
                                    scope.error_message = data.message;
                                } else {
                                    scope.error_message = 'The form has been successfully submitted.';
                                }
                            } else {
                                // The form failed to submit
                                var message = generic_error;
                                if( typeof(data.message) !== 'undefined' && data.message.length > 0 ) {
                                    message = data.message;
                                }

                                scope.error_message = message;
                            }
                        })
                        .error(function(data, status) {
                            // The post errored out
                            scope.submitting = false;
                            var message = generic_error;

                            if( typeof(data.message) !== 'undefined' && data.message.length > 0 ) {
                                message = data.message;
                            }

                            scope.error_message = message;
                        });
                };

                element.bind('submit', function () {
                    if (form.$valid) {
                        scope.submitting = true;

                        submit_form();

                    }

                    // Cancel the submit
                    return false;
                });
            }
        };
    }]);

    vspot.directive('vspotForm', ['$log', '$timeout', function($log, $timeout){
        // Helper to deal with prefilled data, and any other behaviours we want for form consistency
        return {
            restrict:   'A',
            link: function(scope, form_elem, attrs) {
                if( typeof(attrs.name) === 'undefined' ) {
                    $log.log('Unable to set form events: vspotForm requires the form to have a name attribute.');
                } else {
                    $log.log('vspotForm setting form events.');
                    
                    ng.forEach(form_elem.find('input').not("[type='submit']"), function(elem_html) {
                        var elem = ng.element(elem_html);
                        
                        // listen for pertinent events to trigger input on form element
                        // use timeout to ensure val is populated before triggering 'input'
                        // ** 'change' event fires for Chrome
                        // ** 'DOMAttrModified' fires for Firefox 22.0
                        // ** 'keydown' for Safari  6.0.1
                        // ** 'propertychang' for IE
                        ng.element(elem).on('change.autofill DOMAttrModified.autofill keydown.autofill propertychange.autofill', function (e) {
                            $timeout( function () {
                                // Check to see if the element value has been updated
                                if( elem.val() !== '' ) {
                                    elem.trigger('input');
                                }
                            }, 0);
                        });
                    });
                }
            }
        };
    }]);

    vspot.config(['uibDatepickerConfig', 'uibDatepickerPopupConfig', function (uibDatepickerConfig, uibDatepickerPopupConfig) {
        uibDatepickerConfig.showWeeks          = uibDatepickerPopupConfig.showWeeks           = false;
        uibDatepickerConfig.formatDay          = uibDatepickerPopupConfig.formatDay           = 'd';
        uibDatepickerConfig.formatYear         = uibDatepickerPopupConfig.formatYear          = 'yyyy';
        uibDatepickerConfig.formatMonth        = uibDatepickerPopupConfig.formatMonth         = 'MMM';
        uibDatepickerConfig.formatDayHeader    = uibDatepickerPopupConfig.formatDayHeader     = 'EEE';
        uibDatepickerConfig.formatDayTitle     = uibDatepickerPopupConfig.formatDayTitle      = 'MMMM yyyy';
        uibDatepickerConfig.formatMonthTitle   = uibDatepickerPopupConfig.formatMonthTitle    = 'yyyy';
        uibDatepickerConfig.maxMode            = uibDatepickerPopupConfig.maxMode             = 'month';
    }]);

    vspot.filter('unsafe', ['$sce', function ($sce) {
        return function (val) {
            return $sce.trustAsHtml(val);
        };
    }]);

    // Mostly borrowed from here: https://github.com/a8m/angular-filter/blob/master/src/_filter/string/ucfirst.js
    vspot.filter('ucfirst', [function() {
        return function(input) {
            return typeof(input) === 'string' ?
                    input.
                        split(' ').
                        map(function (ch) {
                            return ch.charAt(0).toUpperCase() + ch.substring(1);
                        }).
                        join(' ')
                : input;
        };
    }]);

    // Adds functions to all scopes
    vspot.run(['$rootScope', '$log', 'localize', function($rootScope, $log, localize) {
        /*
         * getLocalizedString: wrapper function so localize is availalbe everywhere.
         */
        $rootScope.getLocalizedString = function(str, params) {
            return localize.getLocalizedString(str, params);
        };

        /*
         * iso8601Date:
         * Helper function. Take a date object. If it's a string, then replace
         * slashes with dashes to make it comply with ISO8601 formatting. Ruby
         * likes to return strings with slashes, '2038/01/01', but XDate needs
         * them to have dashes, '2038-01-01'.
         * http://en.wikipedia.org/wiki/ISO_8601
         * Examples:
         *   "2011-09-05"
         *   "2011-09-05T12:30:00"
         *   "2011-09-05T12:30:00-07:00"
         *   "2011-09-05T12:30:00Z"
         */
        var iso8601Date = function(date) {
            if( typeof(date) === 'string' ) {
                var dateArr = date.split(' ');
                if( dateArr.length === 1 ) {
                    // The date didn't split into an array. We *probably* got a date in the format of "2014/09/08" or ""2014-09-08"
                    if( date.indexOf('/') !== -1 ) {
                        // we have slashes
                        date = date.replace(/\//g, '-');
                    }
                }
                //  else {
                //     $log.log("iso8601Date received the date in an unknown format, returning unchanged. Received:", date);
                // }
            }

            return date;
        };

        // Publish to root scope
        $rootScope.iso8601Date = iso8601Date;

        /*
         *  Formats a date.
         *
         *  @param:  date - [REQUIRED] [string]
         *  @param:  type - [OPTIONAL] The desired format of the date [string]
         *
         *  Available types:
         *      default: Returns the most common case, basic day format [string]
         *      month:   Returns the date used for months [string]
         *      raw:     Returns the date object [object]
         *
         *      If no type is found, it is assumed that type is a format string, such as 'yyyy-MM-dd'.
         *
         *  @returns A string of the date, an date library object, and if nothing
         *           is defined, return an empty string, as the common case for the
         *           argument not being defined is iteration over a data structure,
         *           not a misuse of the function.
         */
        $rootScope.vspotFormatDate = function (date, type) {
            if (date && date !== '2038/01/01') {
                if (!type || type === 'default') {
                    return $rootScope.vspotCreateDate(date).toString('ddd MMM d, yyyy');
                } else if (type === 'short') {
                    return $rootScope.vspotCreateDate(date).toString('MMM dd, yyyy');
                } else if (type === 'month') {
                    return $rootScope.vspotCreateDate(date).toString('MMMM yyyy');
                } else if (type === 'lockdate') {
                    return $rootScope.vspotCreateDate(date).toString('MMMM dS, yyyy');
                } else if (type === 'locktime') {
                    return $rootScope.vspotCreateDate(date).toString('h:mmtt MMMM dS, yyyy');
                } else if (type === 'native') {
                    return $rootScope.vspotCreateDate(date).toDateString();
                } else if (type === 'iso') {
                    return $rootScope.vspotCreateDate(date).toISOString();
                } else if (type === 'raw') {
                    return $rootScope.vspotCreateDate(date);
                } else if (type === 'calendar' || (type === 'api')) {
                    return $rootScope.vspotCreateDate(date).toString('yyyy-MM-dd');
                } else {
                    return $rootScope.vspotCreateDate(date).toString(type);
                }
            } else {
                return '';
            }
        };

        /*
         *  Formats a date into a time string.
         *
         *  @param:  date - [REQUIRED] string, JS date, or XDate object...anything XDate can parse.
         *                             For MomentJS, please convert to string first using 'YYYY-MM-DD'
         *  @param:  type - [OPTIONAL] The desired format of the date [string]
         *
         *  Available types:
         *      default:    Returns the most common case, basic time format [string]
         *      mil:        Returns the military formatted time [string]
         *
         *  @returns A string of the time, or return an empty string, as the common case for the
         *           argument not being defined is iteration over a data structure,
         *           not a misuse of the function.
         */
        $rootScope.vspotFormatTime = function (date, type) {
            if (date) {
                if (!type || type === 'default') {
                    return $rootScope.vspotCreateDate(date).toString('h:mmtt');
                } else if (type === 'mil') {
                    return $rootScope.vspotCreateDate(date).toString('HH:mm');
                } else {
                    return '';
                }
            } else {
                return '';
            }
        };

        /*
         *  Returns a new date, in whatever library we decide to use.
         *
         *  @param:  date - [REQUIRED] string, JS date, or XDate object...anything XDate can parse.
         *                             For MomentJS, please convert to string first using 'YYYY-MM-DD'
         */
        $rootScope.vspotCreateDate = function (date) {
            if (date) {
                if( date instanceof XDate ) {
                    // It's already an XDate so just return it
                    return new XDate(date);
                } else if( moment.isMoment( date ) ) {
                    // It's a moment, convert to XDate
                    return new XDate( date.toDate() );
                } else {
                    // Assume it's a string
                    date = iso8601Date(date);
                    return new XDate(date);
                }
            } else {
                return new XDate().clearTime();
            }
        };

        /*
         *  Returns the minute (0-59).
         *
         *  @param: date - [REQUIRED] The date object.
         */
        $rootScope.vspotMinute = function (date) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            return date.getMinutes();
        };

        /*
         *  Returns the hour of the day (0-23).
         *
         *  @param: date - [REQUIRED] The date object.
         */
        $rootScope.vspotHourOfDay = function (date) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            return date.getHours();
        };

        /*
         *  Returns the day of the week (0-6).
         *
         *  @param: date - [REQUIRED] The date object to get the day of week from.
         *                              0=SUN, 1=MON, 2=TUE, etc...
         */
        $rootScope.vspotDayOfWeek = function (date) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            return date.getDay();
        };

        /*
         *  Returns true if it's a weekend, false otherwise.
         *
         *  @param: date - [REQUIRED] The date object to get the day of week from.
         */
        $rootScope.isWeekEnd = function(date) {
            var dow = $rootScope.vspotDayOfWeek(date);
            var isWeekend = false;
            if( dow === 0 || dow === 6 ) {
                isWeekend = true;
            }

            return isWeekend;
        };

        /*
         *  Returns true if it's NOT weekend, false otherwise.
         *
         *  @param: date - [REQUIRED] The date object to get the day of week from.
         */
        $rootScope.isWeekDay = function(date) {
            return !$rootScope.isWeekEnd(date);
        };

        /*
         *  NOTE: This function supports the current add jobs ui.
         *
         *  Returns a date that matches some criteria.
         *  Looking for the 'first' 'tuesday' of the month.
         *  Looking for the 'last' 'sunday' of the month.
         *
         *  NOTE: Using Moment!!!!
         *
         *  @param: {int} year - [REQUIRED] 4 digit year
         *  @param: {int} month - [REQUIRED] the month, 0-11.
         *  @param: {str} num - [REQUIRED] Which numbered item we are looking for: 'first', 'second', 'third', 'fourth', 'fifth', 'last'
         *  @param: {str} item - [REQUIRED] The item looking for: 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'day', 'weekday', 'weekendday'
         */
        $rootScope.findRelativeDayOfMonth = function(year, month, num, item) {
            var retVal = false; // Return false if it didn't work

            var dayLookingFor = [];
            switch(item) {
                case 'sunday':
                    dayLookingFor = [0];
                    break;
                case 'monday':
                    dayLookingFor = [1];
                    break;
                case 'tuesday':
                    dayLookingFor = [2];
                    break;
                case 'wednesday':
                    dayLookingFor = [3];
                    break;
                case 'thursday':
                    dayLookingFor = [4];
                    break;
                case 'friday':
                    dayLookingFor = [5];
                    break;
                case 'saturday':
                    dayLookingFor = [6];
                    break;
                case 'day':
                    dayLookingFor = [0,1,2,3,4,5,6];
                    break;
                case 'weekday':
                    dayLookingFor = [1,2,3,4,5];
                    break;
                case 'weekendday':
                    dayLookingFor = [0,6];
                    break;
            }

            // Pad the month
            var y = year.toString();
            var m = ('0' + (month+1).toString()).slice(-2);
            var dStr = y + '-' + m + '-01';

            var start = moment(dStr);
            var end = moment(start).endOf('month').startOf('day');

            if( num === 'last' ) {
                // Special case
                if( dayLookingFor.length === 1 ) {
                    // We are looking for a specific day
                    if( end.day() < dayLookingFor[0] ) {
                        retVal = $rootScope.vspotCreateDate( end.day( dayLookingFor[0] - 7 ) );
                    } else {
                        retVal = $rootScope.vspotCreateDate( end.day( dayLookingFor[0] ) );
                    }
                } else if( dayLookingFor.indexOf( end.day() ) !== -1 ) {
                    // Return the last day of the month is in the set we are looking for, so return it.
                    // (this completely covers the 'day' case).
                    retVal = $rootScope.vspotCreateDate( end );
                } else if( item === 'weekday' ) {
                    // The last day of the month must be a saturday or sunday
                    // so return the last friday.
                    if( end.day() < 5 ) {
                        retVal = $rootScope.vspotCreateDate( end.day( 5 - 7 ) );
                    } else {
                        retVal = $rootScope.vspotCreateDate( end.day( 5 ) );
                    }
                } else if( item === 'weekendday' ) {
                    // The last day of the month must be a m-f
                    // so return the last sunday
                    retVal = $rootScope.vspotCreateDate( end.day( 0 ) );
                }
            } else {
                // Iterate over the month
                var current = moment(start); // What are we operating on
                var countSun    = 0;
                var countMon    = 0;
                var countTue    = 0;
                var countWed    = 0;
                var countThr    = 0;
                var countFri    = 0;
                var countSat    = 0;
                var countDay    = 0;
                var countWDay   = 0;
                var countWEDay  = 0;
                var targetCount = 0;

                switch( num ) {
                    case 'first':
                        targetCount = 1;
                        break;
                    case 'second':
                        targetCount = 2;
                        break;
                    case 'third':
                        targetCount = 3;
                        break;
                    case 'fourth':
                        targetCount = 4;
                        break;
                    case 'fifth':
                        targetCount = 5;
                        break;
                    // last handled above - special case
                    default:
                        // Something we can't check for - break out early!
                        return false;
                }

                monthlyIterator:
                    do {
                        // Increment our counters
                        switch( current.day() ) {
                            case 0:
                                // Sunday
                                countDay++;
                                countSun++;
                                countWEDay++;
                                break;
                            case 1:
                                // Monday
                                countDay++;
                                countMon++;
                                countWDay++;
                                break;
                            case 2:
                                // Tuesday
                                countDay++;
                                countTue++;
                                countWDay++;
                                break;
                            case 3:
                                // Wednesday
                                countDay++;
                                countWed++;
                                countWDay++;
                                break;
                            case 4:
                                // Thursday
                                countDay++;
                                countThr++;
                                countWDay++;
                                break;
                            case 5:
                                // Friday
                                countDay++;
                                countFri++;
                                countWDay++;
                                break;
                            case 6:
                                // Saturday
                                countDay++;
                                countSat++;
                                countWEDay++;
                                break;
                        }

                        // Now see if we reached our goal
                        switch( item ) {
                            case 'sunday':
                                if( countSun >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'monday':
                                if( countMon >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'tuesday':
                                if( countTue >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'wednesday':
                                if( countWed >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'thursday':
                                if( countThr >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'friday':
                                if( countFri >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'saturday':
                                if( countSat >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'day':
                                if( countDay >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'weekday':
                                if( countWDay >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                            case 'weekendday':
                                if( countWEDay >= targetCount ) {
                                    retVal = $rootScope.vspotCreateDate( current );
                                    break monthlyIterator;
                                }
                                break;
                        }

                        // Add one day to the current
                        current.add(1, 'day');
                    } while( current.isBefore(end) );
            }

            return retVal;
        };

        /*
         *  NOTE: This function supports the new add jobs ui.
         *
         *  Returns a date that matches some criteria.
         *  Looking for the 'first' 'tuesday' of the month.
         *  Looking for the 'last' 'sunday' of the month.
         *
         *  NOTE: Using Moment!!!!
         *
         *  @param: {int} year - [REQUIRED] 4 digit year
         *  @param: {int} month - [REQUIRED] the month, 0-11.
         *  @param: {int} num - [REQUIRED] Which numbered item we are looking for: first, second, third, fourth, last
         *  @param: {int} item - [REQUIRED] The item looking for: day of the week, represented by a number
         */
        $rootScope.findRelativeDayOfMonthNew = function(year, month, num, item) {
            var retVal = false; // Return false if it didn't work
            var dayLookingFor = item;

            // Pad the month
            var y = year.toString();
            var m = ('0' + (month+1).toString()).slice(-2);
            var dStr = y + '-' + m + '-01';

            var start = moment(dStr);
            var end = moment(start).endOf('month').startOf('day');
            // last
            if( num === 5 ) {
                // Special case
                // We are looking for a specific day
                if( end.day() < dayLookingFor ) {
                    retVal = $rootScope.vspotCreateDate( end.day( dayLookingFor - 7 ) );
                } else {
                    retVal = $rootScope.vspotCreateDate( end.day( dayLookingFor ) );
                }
            } else {
                // Iterate over the month
                var current = moment(start); // What are we operating on
                var countSun    = 0;
                var countMon    = 0;
                var countTue    = 0;
                var countWed    = 0;
                var countThr    = 0;
                var countFri    = 0;
                var countSat    = 0;
                var countDay    = 0;
                var countWDay   = 0;
                var countWEDay  = 0;
                var targetCount = num;

                monthlyIterator:
                do {
                    // Increment our counters
                    switch( current.day() ) {
                        case 0:
                            // Sunday
                            countDay++;
                            countSun++;
                            countWEDay++;
                            break;
                        case 1:
                            // Monday
                            countDay++;
                            countMon++;
                            countWDay++;
                            break;
                        case 2:
                            // Tuesday
                            countDay++;
                            countTue++;
                            countWDay++;
                            break;
                        case 3:
                            // Wednesday
                            countDay++;
                            countWed++;
                            countWDay++;
                            break;
                        case 4:
                            // Thursday
                            countDay++;
                            countThr++;
                            countWDay++;
                            break;
                        case 5:
                            // Friday
                            countDay++;
                            countFri++;
                            countWDay++;
                            break;
                        case 6:
                            // Saturday
                            countDay++;
                            countSat++;
                            countWEDay++;
                            break;
                    }

                    // Now see if we reached our goal
                    switch( item ) {
                        case 0:
                            if( countSun >= targetCount ) {
                                retVal = $rootScope.vspotCreateDate( current );
                                break monthlyIterator;
                            }
                            break;
                        case 1:
                            if( countMon >= targetCount ) {
                                retVal = $rootScope.vspotCreateDate( current );
                                break monthlyIterator;
                            }
                            break;
                        case 2:
                            if( countTue >= targetCount ) {
                                retVal = $rootScope.vspotCreateDate( current );
                                break monthlyIterator;
                            }
                            break;
                        case 3:
                            if( countWed >= targetCount ) {
                                retVal = $rootScope.vspotCreateDate( current );
                                break monthlyIterator;
                            }
                            break;
                        case 4:
                            if( countThr >= targetCount ) {
                                retVal = $rootScope.vspotCreateDate( current );
                                break monthlyIterator;
                            }
                            break;
                        case 5:
                            if( countFri >= targetCount ) {
                                retVal = $rootScope.vspotCreateDate( current );
                                break monthlyIterator;
                            }
                            break;
                        case 6:
                            if( countSat >= targetCount ) {
                                retVal = $rootScope.vspotCreateDate( current );
                                break monthlyIterator;
                            }
                            break;
                    }

                    // Add one day to the current
                    current.add(1, 'day');
                } while( current.isBefore(end) );
            }

            return retVal;
        };


        /*
         *  Returns the 4 digit year
         *
         *  @param: date - [REQUIRED] The date object to get the day of year from.
         */
        $rootScope.vspotYear = function (date) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            return date.getFullYear();
        };

        /*
         *  Returns the month (0-11);
         *
         *  @param: date - [REQUIRED] The date object to get the day of month from.
         */
        $rootScope.vspotMonth = function (date) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            return date.getMonth();
        };

        /*
         *  Returns the day of the month (1-31).
         *
         *  @param: date - [REQUIRED] The date object to get the day of month from.
         */
        $rootScope.vspotDayOfMonth = function (date) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            return date.getDate();
        };

        /*
         *  Returns a native JS date, given an XDate object.
         *
         *  @param: date - [REQUIRED] The XDate object to convert to a native date.
         */
        $rootScope.vspotNativeJSDate = function (date) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            return date.toDate();
        };

        /*
         *  Returns a copy of the given an XDate object. Sometimes we need to make sure we are not
         *  operating on the original.
         *
         *  @param: date - [REQUIRED] The XDate object to clone.
         */
        $rootScope.vspotCloneDate = function (date) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            return date.clone();
        };

        /*
         *  Returns the XDate object with the date updated.
         *
         *  @param: date - [REQUIRED] The XDate object to set.
         *  @param: year - [OPTIONAL] int value for year, should be 4 digits, unless you want it to be 2000 years ago.
         *  @param: month - [OPTIONAL] int value for month. Month is zero-indexed, meaning Jan=0, Feb=1, Mar=2, etc.
         *  @param: day - [OPTIONAL] int value for day (1-31).
         *
         */
        $rootScope.vspotSetDate = function (date, year, month, day) {
            // 0 is considered blank so be careful of that
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            if( year === 0 || !global_helper.blank(year) ) {
                date.setFullYear(year);
            }
            if( month === 0 || !global_helper.blank(month) ) {
                date.setMonth(month);
            }
            if( !global_helper.blank(day) ) {
                date.setDate(day);
            }
            return date;
        };

        /*
         *  Returns the XDate object with the time updated.
         *
         *  @param: date - [REQUIRED] The XDate object to set.
         *  @param: hours - [OPTIONAL] int value for hours, in 24 hour time.
         *  @param: minutes - [OPTIONAL] int value for minutes.
         */
        $rootScope.vspotSetTime = function (date, hours, minutes) {
            // 0 is considered blank so be careful of that
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            if( hours === 0 || !global_helper.blank(hours) ) {
                date.setHours(hours);
            }
            if( minutes === 0 || !global_helper.blank(minutes) ) {
                date.setMinutes(minutes);
            }
            return date;
        };

        /*
         *  Returns a unix timestamp for busting cache.
         */
        $rootScope.vspotCacheBuster = function () {
            return new XDate().getTime();
        };

        /*
         *  Returns yesterday. Rather specific, but better to do it here so that when we change to
         *  Moment.js we can easily catch this case.
         *
         *  @param: date - [OPTIONAL]   If provided, return the date before the given date, otherwise
         *                              return the date before today
         */
        $rootScope.vspotYesterday = function (date) {
            if( date ) {
                date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
                return date.addDays(-1);
            } else {
                return new XDate().addDays(-1);
            }
        };

        /*
         *  Returns tomorrow. Rather specific, but better to do it here so that when we change to
         *  Moment.js we can easily catch this case.
         *
         *  @param: date - [OPTIONAL]   If provided, return the date after the given date, otherwise
         *                              return the date after today
         */
        $rootScope.vspotTomorrow = function (date) {
            if( date ) {
                date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
                return date.addDays(1);
            } else {
                return new XDate().addDays(1);
            }
        };

        /*
         *  Returns the date moved in time.
         *
         *  @param: date - [REQUIRED]  date to start with.
         *  @param: toShift - [REQUIRED] int how far to move the date, can be positive for future, negative for past.
         *  @param: unit - [REQUIRED] str - the type of unit to add or subtract ('millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'year')
         */
        $rootScope.vspotShiftDate = function (date, toShift, unit) {
            date = $rootScope.vspotCreateDate( date ); // make sure it's XDate
            switch( unit ) {
                case 'millisecond':
                case 'milliseconds':
                    date.addMilliseconds( toShift );
                    break;
                case 'second':
                case 'seconds':
                    date.addSeconds( toShift );
                    break;
                case 'minute':
                case 'minutes':
                    date.addMinutes( toShift );
                    break;
                case 'hour':
                case 'hours':
                    date.addHours( toShift );
                    break;
                case 'day':
                case 'days':
                    date.addDays( toShift );
                    break;
                case 'week':
                case 'weeks':
                    date.addWeeks( toShift );
                    break;
                case 'month':
                case 'months':
                    date.addMonths( toShift );
                    break;
                case 'year':
                case 'years':
                    date.addYears( toShift );
                    break;
            }
            
            return date;
        };

        /*
         *  Returns the difference between two dates. Result can be negative.
         *
         *  @param: dateFrom - [REQUIRED] date to start with.
         *  @param: dateTo - [REQUIRED] date to diff to
         *  @param: unit - [REQUIRED] str - the type of unit to add or subtract ('millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'year')
         */
        $rootScope.vspotDiffDates = function (dateFrom, dateTo, unit) {
            dateFrom = $rootScope.vspotCreateDate( dateFrom ); // make sure it's XDate
            dateTo = $rootScope.vspotCreateDate( dateTo ); // make sure it's XDate
            switch( unit ) {
                case 'millisecond':
                case 'milliseconds':
                    return dateFrom.diffMilliseconds( dateTo );
                    break;
                case 'second':
                case 'seconds':
                    return dateFrom.diffSeconds( dateTo );
                    break;
                case 'minute':
                case 'minutes':
                    return dateFrom.diffMinutes( dateTo );
                    break;
                case 'hour':
                case 'hours':
                    return dateFrom.diffHours( dateTo );
                    break;
                case 'day':
                case 'days':
                    return dateFrom.diffDays( dateTo );
                    break;
                case 'week':
                case 'weeks':
                    return dateFrom.diffWeeks( dateTo );
                    break;
                case 'month':
                case 'months':
                    return dateFrom.diffMonths( dateTo );
                    break;
                case 'year':
                case 'years':
                    return dateFrom.diffYears( dateTo );
                    break;
            }
        };

        /*
         *  Compares two dates and returns 0, 1, or -1. Please use the auxiliary functions,
         *  vspotDateIsAfter, vspotDateIsBefore, and vspotDateIsEqual instead, for easier to
         *  read code. If you just want to know if the date is today or later, use vspotDateIsCurrent.
         *
         *  @param:  date1 - [REQUIRED] string, JS date, or XDate object...anything XDate can parse.
         *                              For MomentJS, please convert to string first using 'YYYY-MM-DD'
         *  @param:  date2 - [REQUIRED] string, JS date, or XDate object...anything XDate can parse.
         *                              For MomentJS, please convert to string first using 'YYYY-MM-DD'
         *  @param:  type  - [OPTIONAL] If you want to compare date and time, use 'exact'. Defaults to
         *                              comparing the day, not the time.
         *
         *  @returns 0, 1, or -1. (date1 === date2, date1 < date2, date1 > date2)
         */
        $rootScope.vspotCompareDates = function (date1, date2, type) {
            if (date1 && date2) {
                var d1 = $rootScope.vspotCreateDate(date1);
                var d2 = $rootScope.vspotCreateDate(date2);
                var diff;

                if (!type) {
                    diff = d1.diffDays(d2);
                } else if (type === 'exact') {
                    diff = d1.diffMilliseconds(d2);
                } else if (type === 'month') {
                    // round down so that dates within the same month show up as diff === 0
                    diff = Math.floor(d1.diffMonths(d2));
                } else if (type === 'year') {
                    // round down so that dates within the same year show up as diff === 0
                    diff = Math.floor(d1.diffYears(d2));
                }

                // d2 > d1
                if (diff > 0) {
                    return 1;
                } else if (diff < 0) {
                    // d1 > d2
                    return -1;
                } else {
                    // d1 == d2
                    return 0;
                }
            } else {
                return false;
            }
        };

        /*
         *  Compares two dates to see if date1 comes after date2.
         *
         *  Refer to vspotDateCompareDates for parameter info.
         *
         *  @returns true if date1 comes after date2.
         */
        $rootScope.vspotDateIsAfter = function(date1, date2, type) {
            if (date1 && date2) {
                return $rootScope.vspotCompareDates(date1, date2, type) < 0;
            } else {
                return false;
            }
        };

        /*
         *  Compares two dates to see if date1 comes before date2.
         *
         *  Refer to vspotDateCompareDates for parameter info.
         *
         *  @returns true if date1 comes before date2.
         */
        $rootScope.vspotDateIsBefore = function(date1, date2, type) {
            if (date1 && date2) {
                return $rootScope.vspotCompareDates(date1, date2, type) > 0;
            } else {
                return false;
            }
        };

        /*
         *  Compares two dates to see if date1 is equal to date2.
         *
         *  Refer to vspotDateCompareDates for parameter info.
         *
         *  @returns true if date1 comes is equal to date2.
         */
        $rootScope.vspotDateIsEqual = function(date1, date2, type) {
            if (date1 && date2) {
                return $rootScope.vspotCompareDates(date1, date2, type) === 0;
            } else {
                return false;
            }
        };

        /*
         *  Is the given date in the present or future?
         *
         *  Refer to vspotDateCompareDates for parameter info.
         *
         *  @returns true if the date is today or later.
         */
        $rootScope.vspotDateIsCurrent = function(date) {
            if (date) {
                var diff = $rootScope.vspotCompareDates(date, $rootScope.vspotCreateDate());
                return diff <= 0;
            } else {
                return false;
            }
        };

        /*
         *  Is the given date in the past?
         *
         *  Refer to vspotDateCompareDates for parameter info.
         *
         *  @returns true if the date is before today.
         */
        $rootScope.vspotDateIsPast = function(date) {
            return $rootScope.vspotDateIsAfter($rootScope.vspotCreateDate(), date);
        };

        /*
         *  Converts a date to the most common use case string.
         *
         *  @param:  date  - [REQUIRED] string, JS date, or XDate object...anything XDate can parse.
         *                               For MomentJS, please convert to string first using 'YYYY-MM-DD'
         *
         *  @returns A string in the most common use case format.
         */
        $rootScope.vspotDateToString = function (date) {
            if (date) {
                return $rootScope.vspotCreateDate(date).toString('yyyy-MM-dd');
            } else {
                return '';
            }
        };

        /*
         * incrementallyProcess:
         *      Throttle processing large amounts of data to prevent the unresponsive
         *      script error from the browser.
         * @param {fn}    [REQUIRED] workerCallback        The function that will be called on each piece of data
         * @param {array} [REQUIRED] data                  The data to be processed.
         * @param {int}   [REQUIRED] chunkSize             The number representing how much of the data will be
         *                                                 processed in a single iteration.
         * @param {int}   [REQUIRED] timeout               The number of milliseconds to wait between iterations. Recommended: 20
         * @param {fn}    [OPTIONAL] completionCallback    The function that will be called when all of the data
         *                                                 is done being processed.
         * @param {obj}   [OPTIONAL] workerArguments       An object containing all the necessary arguments to
         *                                                 process a piece of data.
         * @param {obj}   [OPTIONAL] completionArguments   An object containing any arguments required by
         *                                                 the completionCallback.
         */
        $rootScope.incrementallyProcess = function (workerCallback, data, chunkSize, timeout, completionCallback, workerArguments, completionArguments) {
            var itemIndex = 0;
            function incrementallyProcessHelper () {
                var remainingDataLength = (data.length - itemIndex);
                var currentChunkSize = (remainingDataLength >= chunkSize) ? chunkSize : remainingDataLength;
                var item;
                if(itemIndex < data.length) {
                    while(currentChunkSize--) {
                        item = data[itemIndex];
                        workerCallback(item, itemIndex, workerArguments);
                        itemIndex++;
                    }
                    setTimeout(incrementallyProcessHelper, timeout);
                } else if(completionCallback) {
                    completionCallback(completionArguments);
                }
            }

            incrementallyProcessHelper();
        };

        /*
         *  Combines two data sets with common attributes.
         *  See http://learnjsdata.com/combine_data.html
         *
         *  @param lookupTable [OBJECT]
         *  @param mainTable   [OBJECT]
         *  @param lookupKey   [STRING]
         *  @param mainKey     [STRING]
         *  @param select      [FUNCTION]
         */
        $rootScope.vspotJoinData = function(lookupTable, mainTable, lookupKey, mainKey, select) {
            var mainTableRow, lookupTableRow;
            var lookupIndex = [],
                output = [];

            // loop through each of the lookupTable items to build the lookupIndex
            for (var i=0, iLen=lookupTable.length; i < iLen; i++) {
                lookupTableRow = lookupTable[i];
                lookupIndex[ lookupTableRow[lookupKey] ] = lookupTableRow; // create an index for the lookup table
            }

            // loop through mainTable items
            for (var j=0, jLen=mainTable.length; j < jLen; j++) {
                mainTableRow = mainTable[j];
                lookupTableRow = lookupIndex[ mainTableRow[mainKey] ]; // get corresponding row from lookupTable
                output.push(select(mainTableRow, lookupTableRow)); // select only the columns you need
            }

            return output;
        };

        /*
         * froalaOptions:
         *      Set up site-wide options for the editor. They can be overridden on
         *      a case by case basis.
         */
        var toolBarRow = ['bold', 'italic', 'underline', '|', 'paragraphFormat', 'clearFormatting', 'color', 'align', '|', 'formatOL', 'formatUL', 'insertLink', 'html', '|', 'undo', 'redo'];
        // for email (in your local copy): ['bold', 'italic', 'underline', '|', 'align', '|', 'insertLink', 'insertImage', '|', 'undo', 'redo']
        $rootScope.froalaOptions = {
            // thy suggest CC4C2A3F3C2G1tA6B4C3A3C7D1A1E1E4I3ogA-16qymiF2J-7yk== but that does not work
            key: 'tF1C4A1C6mD6F5B4H3E1B9A5C3C4C4g1nwxntrE4gjk==',
            // for large devices (≥ 1200px):
            toolbarButtons: toolBarRow,
            // for medium devices (≥ 992px):
            toolbarButtonsMD: toolBarRow,
            // for small devices (≥ 768px):
            toolbarButtonsSM: toolBarRow,
            // for extra small devices (< 768px):
            toolbarButtonsXS: toolBarRow,
            paragraphFormat: {
                BLOCKQUOTE: 'Block Quote',
                CITE:       'Citation',
                N:          'Paragraph',
                PRE:        'Preformatted',
                SMALL:      'Small'
            },
            events : {
                // NOTE: If you override this event, you must call htmlCounter.update yourself!!!!!!!!!!!!!!!!!!!!!!!!!!
                'froalaEditor.html.set': function (e, editor) {
                    // Update the character count
                    $(this).froalaEditor('events.trigger', 'htmlCounter.update');
                }
            },
            charCounterCount:   false, // Don't use this if enabling ours
            htmlCounterCount:   true, // Custom VSpot HTML counter
            heightMin:          200,
            heightMax:          400,
            imagePaste:         false, // Turn off pasting from clip board
            imageInsertButtons: ['imageBack', '|', 'imageByURL'], // Default to only support URL
            imageDefaultWidth:  0, // No default width
            codeMirror:         false, // We aren't loading it
            placeholderText:    "Enter text here", // Default placeholer text... can be overridden.
            // Link plug-in settings
            linkAutoPrefix:     '', // Don't prefix links with anything (http:// is the default)
            linkList:           [], // Dont' give a list of predefined links
            linkText:           true, // Allow users to edit the link text in the link editor
            imageUploadRemoteUrls: false // Truely evil, on by default,
                                         // replaces all images with src properties to https://i.froala.com/download @see https://github.com/froala/wysiwyg-editor/issues/3229
        };

        // return true if the activity is not locked
        $rootScope.activityIsAllowingParticipants = function (activity) {
            return !activity.is_allowed_to_enter || activity.is_allowed_to_enter && (activity.is_allowed_to_enter.status || (!activity.is_allowed_to_enter.status && activity.is_allowed_to_enter.code !== '_ANONYMOUS_LOGIN_EXPIRED_ERROR_' && activity.is_allowed_to_enter.code !== '_ANONYMOUS_LOGIN_LOCKED_ERROR_'));
        };

        // Etc Timezone list. NOTE: The sign is backwards from what you expect!
        // Etc says "how many hours is GMT ahead or behind me?"
        // UTC says "how many hours am I ahead of or behind UTC?"
        $rootScope.tzList = {
            'Etc/GMT+12':   '_TzMinus12_',
            'Etc/GMT+11':   '_TzMinus11_',
            'Etc/GMT+10':   '_TzMinus10_',
            'Etc/GMT+9':    '_TzMinus9_',
            'Etc/GMT+8':    '_TzMinus8_',
            'Etc/GMT+7':    '_TzMinus7_',
            'Etc/GMT+6':    '_TzMinus6_',
            'Etc/GMT+5':    '_TzMinus5_',
            'Etc/GMT+4':    '_TzMinus4_',
            'Etc/GMT+3':    '_TzMinus3_',
            'Etc/GMT+2':    '_TzMinus2_',
            'Etc/GMT+1':    '_TzMinus1_',
            'Etc/UTC':      '_TzUTC_',
            'Etc/GMT-1':    '_TzPlus1_',
            'Etc/GMT-2':    '_TzPlus2_',
            'Etc/GMT-3':    '_TzPlus3_',
            'Etc/GMT-4':    '_TzPlus4_',
            'Etc/GMT-5':    '_TzPlus5_',
            'Etc/GMT-6':    '_TzPlus6_',
            'Etc/GMT-7':    '_TzPlus7_',
            'Etc/GMT-8':    '_TzPlus8_',
            'Etc/GMT-9':    '_TzPlus9_',
            'Etc/GMT-9:30': '_TzPlus930_',
            'Etc/GMT-10':   '_TzPlus10_',
            'Etc/GMT-11':   '_TzPlus11_',
            'Etc/GMT-12':   '_TzPlus12_',
            'Etc/GMT-13':   '_TzPlus13_'
        };

        // New timezone list, taken from ruby (with ActiveSupport::TimeZone::MAPPING)
        // This is used by the activity lock for timezones. BUT the UTC ones are really not valid - you can't map a UTC
        // to a timezone without a date, i.e. Etc/GMT+5':    '(UTC-05:00) US Eastern Standard Time', is cute
        // but when it changes to Daylight savings time that will not be good for them.
        // So now we have a new list below that pulls the latest timezone info and does not add the fake UTC ones
        //
        // !! @deprecated !!
        // keeping this around because a bunch of people chose some of the invalid timezones for activity locking and
        // we need to figure out what to do with those.
        $rootScope.tzListNew = {
            'Pacific/Midway': 'International Date Line West',
            // 'Pacific/Midway': 'Midway Island',
            'Pacific/Pago_Pago': 'American Samoa',
            'Pacific/Honolulu': 'Hawaii',
            'America/Juneau': 'Alaska',
            'America/Los_Angeles': 'Pacific Time (US & Canada)',
            'America/Tijuana': 'Tijuana',
            'America/Denver': 'Mountain Time (US & Canada)',
            'America/Phoenix': 'Arizona',
            'America/Chihuahua': 'Chihuahua',
            'America/Mazatlan': 'Mazatlan',
            'America/Chicago': 'Central Time (US & Canada)',
            'America/Regina': 'Saskatchewan',
            // 'America/Mexico_City': 'Guadalajara',
            'America/Mexico_City': 'Mexico City',
            'America/Monterrey': 'Monterrey',
            'America/Guatemala': 'Central America',
            'America/New_York': 'Eastern Time (US & Canada)',
            'America/Indiana/Indianapolis': 'Indiana (East)',
            'America/Bogota': 'Bogota',
            'America/Lima': 'Lima',
            // 'America/Lima': 'Quito',
            'America/Halifax': 'Atlantic Time (Canada)',
            'America/Caracas': 'Caracas',
            'America/La_Paz': 'La Paz',
            'America/Santiago': 'Santiago',
            'America/St_Johns': 'Newfoundland',
            'America/Sao_Paulo': 'Brasilia',
            'America/Argentina/Buenos_Aires': 'Buenos Aires',
            'America/Montevideo': 'Montevideo',
            'America/Guyana': 'Georgetown',
            'America/Godthab': 'Greenland',
            'Atlantic/South_Georgia': 'Mid-Atlantic',
            'Atlantic/Azores': 'Azores',
            'Atlantic/Cape_Verde': 'Cape Verde Is.',
            'Europe/Dublin': 'Dublin',
            // 'Europe/London': 'Edinburgh',
            'Europe/Lisbon': 'Lisbon',
            'Europe/London': 'London',
            'Africa/Casablanca': 'Casablanca',
            'Africa/Monrovia': 'Monrovia',
            'Etc/UTC': 'UTC',
            'Europe/Belgrade': 'Belgrade',
            'Europe/Bratislava': 'Bratislava',
            'Europe/Budapest': 'Budapest',
            'Europe/Ljubljana': 'Ljubljana',
            'Europe/Prague': 'Prague',
            'Europe/Sarajevo': 'Sarajevo',
            'Europe/Skopje': 'Skopje',
            'Europe/Warsaw': 'Warsaw',
            'Europe/Zagreb': 'Zagreb',
            'Europe/Brussels': 'Brussels',
            'Europe/Copenhagen': 'Copenhagen',
            'Europe/Madrid': 'Madrid',
            'Europe/Paris': 'Paris',
            'Europe/Amsterdam': 'Amsterdam',
            'Europe/Berlin': 'Berlin',
            // 'Europe/Berlin': 'Bern',
            'Europe/Rome': 'Rome',
            'Europe/Stockholm': 'Stockholm',
            'Europe/Vienna': 'Vienna',
            'Africa/Algiers': 'West Central Africa',
            'Europe/Bucharest': 'Bucharest',
            'Africa/Cairo': 'Cairo',
            'Europe/Helsinki': 'Helsinki',
            'Europe/Kiev': 'Kyiv',
            'Europe/Riga': 'Riga',
            'Europe/Sofia': 'Sofia',
            'Europe/Tallinn': 'Tallinn',
            'Europe/Vilnius': 'Vilnius',
            'Europe/Athens': 'Athens',
            'Europe/Istanbul': 'Istanbul',
            'Europe/Minsk': 'Minsk',
            'Asia/Jerusalem': 'Jerusalem',
            'Africa/Harare': 'Harare',
            'Africa/Johannesburg': 'Pretoria',
            'Europe/Kaliningrad': 'Kaliningrad',
            'Europe/Moscow': 'Moscow',
            // 'Europe/Moscow': 'St. Petersburg',
            'Europe/Volgograd': 'Volgograd',
            'Europe/Samara': 'Samara',
            'Asia/Kuwait': 'Kuwait',
            'Asia/Riyadh': 'Riyadh',
            'Africa/Nairobi': 'Nairobi',
            'Asia/Baghdad': 'Baghdad',
            'Asia/Tehran': 'Tehran',
            // 'Asia/Muscat': 'Abu Dhabi',
            'Asia/Muscat': 'Muscat',
            'Asia/Baku': 'Baku',
            'Asia/Tbilisi': 'Tbilisi',
            'Asia/Yerevan': 'Yerevan',
            'Asia/Kabul': 'Kabul',
            'Asia/Yekaterinburg': 'Ekaterinburg',
            // 'Asia/Karachi': 'Islamabad',
            'Asia/Karachi': 'Karachi',
            'Asia/Tashkent': 'Tashkent',
            // 'Asia/Kolkata': 'Chennai',
            'Asia/Kolkata': 'Kolkata',
            // 'Asia/Kolkata': 'Mumbai',
            // 'Asia/Kolkata': 'New Delhi',
            'Asia/Kathmandu': 'Kathmandu',
            // 'Asia/Dhaka': 'Astana',
            'Asia/Dhaka': 'Dhaka',
            'Asia/Colombo': 'Sri Jayawardenepura',
            'Asia/Almaty': 'Almaty',
            'Asia/Novosibirsk': 'Novosibirsk',
            'Asia/Rangoon': 'Rangoon',
            'Asia/Bangkok': 'Bangkok',
            // 'Asia/Bangkok': 'Hanoi',
            'Asia/Jakarta': 'Jakarta',
            'Asia/Krasnoyarsk': 'Krasnoyarsk',
            'Asia/Shanghai': 'Beijing',
            'Asia/Chongqing': 'Chongqing',
            'Asia/Hong_Kong': 'Hong Kong',
            'Asia/Urumqi': 'Urumqi',
            'Asia/Kuala_Lumpur': 'Kuala Lumpur',
            'Asia/Singapore': 'Singapore',
            'Asia/Taipei': 'Taipei',
            'Australia/Perth': 'Perth',
            'Asia/Irkutsk': 'Irkutsk',
            'Asia/Ulaanbaatar': 'Ulaanbaatar',
            'Asia/Seoul': 'Seoul',
            // 'Asia/Tokyo': 'Osaka',
            // 'Asia/Tokyo': 'Sapporo',
            'Asia/Tokyo': 'Tokyo',
            'Asia/Yakutsk': 'Yakutsk',
            'Australia/Darwin': 'Darwin',
            'Australia/Adelaide': 'Adelaide',
            // 'Australia/Melbourne': 'Canberra',
            'Australia/Melbourne': 'Melbourne',
            'Australia/Sydney': 'Sydney',
            'Australia/Brisbane': 'Brisbane',
            'Australia/Hobart': 'Hobart',
            'Asia/Vladivostok': 'Vladivostok',
            'Pacific/Guam': 'Guam',
            'Pacific/Port_Moresby': 'Port Moresby',
            'Asia/Magadan': 'Magadan',
            'Asia/Srednekolymsk': 'Srednekolymsk',
            'Pacific/Guadalcanal': 'Solomon Is.',
            'Pacific/Noumea': 'New Caledonia',
            'Pacific/Fiji': 'Fiji',
            'Asia/Kamchatka': 'Kamchatka',
            'Pacific/Majuro': 'Marshall Is.',
            'Pacific/Auckland': 'Auckland',
            // 'Pacific/Auckland': 'Wellington',
            'Pacific/Tongatapu': 'Nuku\'alofa',
            'Pacific/Fakaofo': 'Tokelau Is.',
            'Pacific/Chatham': 'Chatham Is.',
            'Pacific/Apia': 'Samoa',
            'Etc/GMT+12':   '(UTC-12:00) Baker Island Time',
            'Etc/GMT+11':   '(UTC-11:00) Samoa Standard Time',
            'Etc/GMT+10':   '(UTC-10:00) Hawaii Standard Time',
            'Etc/GMT+9':    '(UTC-09:00) Alaska Standard Time',
            'Etc/GMT+8':    '(UTC-08:00) US Pacific Standard Time',
            'Etc/GMT+7':    '(UTC-07:00) US Mountain Standard Time',
            'Etc/GMT+6':    '(UTC-06:00) US Central Standard Time',
            'Etc/GMT+5':    '(UTC-05:00) US Eastern Standard Time',
            'Etc/GMT+4':    '(UTC-04:00) Atlantic Standard Time',
            'Etc/GMT+3':    '(UTC-03:00) Uruguay Standard Time',
            'Etc/GMT+2':    '(UTC-02:00) Uruguay Summer Time',
            'Etc/GMT+1':    '(UTC-01:00) Eastern Greenland Time',
            'Etc/GMT-1':    '(UTC+01:00) Central European Time',
            'Etc/GMT-2':    '(UTC+02:00) Eastern European Time',
            'Etc/GMT-3':    '(UTC+03:00) Moscow Time',
            'Etc/GMT-4':    '(UTC+04:00) Georgia Standard Time',
            'Etc/GMT-5':    '(UTC+05:00) Pakistan Standard Time',
            'Etc/GMT-6':    '(UTC+06:00) Bangladesh Standard Time',
            'Etc/GMT-7':    '(UTC+07:00) Thailand Standard Time',
            'Etc/GMT-8':    '(UTC+08:00) China Standard Time',
            'Etc/GMT-9':    '(UTC+09:00) Japan Standard Time',
            'Etc/GMT-9:30': '(UTC+09:30) Australian Central Standard Time\'',
            'Etc/GMT-10':   '(UTC+10:00) Eastern Standard Time (Australia)',
            'Etc/GMT-11':   '(UTC+11:00) Solomon Islands Time',
            'Etc/GMT-12':   '(UTC+12:00) New Zealand Standard Time',
            'Etc/GMT-13':   '(UTC+13:00) Tonga Time'
        };

        // new list of valid timezones taken from ruby (with ActiveSupport::TimeZone::MAPPING)
        // @see $rootScope.tzListNew for long comment why that one is wrong.
        $rootScope.tzListNewNew = {
            'Pacific/Midway': "Midway Island",
            'Pacific/Pago_Pago': "American Samoa",
            'Pacific/Honolulu': "Hawaii",
            'America/Juneau': "Alaska",
            'America/Los_Angeles': "Pacific Time (US & Canada)",
            'America/Denver': "Mountain Time (US & Canada)",
            'America/Chicago': "Central Time (US & Canada)",
            'America/New_York': "Eastern Time (US & Canada)",
            'America/Phoenix': "Arizona",
            'America/Indiana/Indianapolis': "Indiana (East)",
            'America/Chihuahua': "Chihuahua",
            'America/Mazatlan': "Mazatlan",
            'America/Tijuana': "Tijuana",
            'America/Regina': "Saskatchewan",
            'America/Mexico_City': "Mexico City",
            'America/Monterrey': "Monterrey",
            'America/Guatemala': "Central America",
            'America/Bogota': "Bogota",
            'America/Lima': "Quito",
            'America/Halifax': "Atlantic Time (Canada)",
            'America/Caracas': "Caracas",
            'America/La_Paz': "La Paz",
            'America/Santiago': "Santiago",
            'America/St_Johns': "Newfoundland",
            'America/Sao_Paulo': "Brasilia",
            'America/Argentina/Buenos_Aires': "Buenos Aires",
            'America/Montevideo': "Montevideo",
            'America/Guyana': "Georgetown",
            'America/Godthab': "Greenland",
            'Atlantic/South_Georgia': "Mid-Atlantic",
            'Atlantic/Azores': "Azores",
            'Atlantic/Cape_Verde': "Cape Verde Is.",
            'Europe/Dublin': "Dublin",
            'Europe/London': "London",
            'Europe/Lisbon': "Lisbon",
            'Africa/Casablanca': "Casablanca",
            'Africa/Monrovia': "Monrovia",
            'Etc/UTC': "UTC",
            'Europe/Belgrade': "Belgrade",
            'Europe/Bratislava': "Bratislava",
            'Europe/Budapest': "Budapest",
            'Europe/Ljubljana': "Ljubljana",
            'Europe/Prague': "Prague",
            'Europe/Sarajevo': "Sarajevo",
            'Europe/Skopje': "Skopje",
            'Europe/Warsaw': "Warsaw",
            'Europe/Zagreb': "Zagreb",
            'Europe/Brussels': "Brussels",
            'Europe/Copenhagen': "Copenhagen",
            'Europe/Madrid': "Madrid",
            'Europe/Paris': "Paris",
            'Europe/Amsterdam': "Amsterdam",
            'Europe/Berlin': "Bern",
            'Europe/Rome': "Rome",
            'Europe/Stockholm': "Stockholm",
            'Europe/Vienna': "Vienna",
            'Africa/Algiers': "West Central Africa",
            'Europe/Bucharest': "Bucharest",
            'Africa/Cairo': "Cairo",
            'Europe/Helsinki': "Helsinki",
            'Europe/Kiev': "Kyiv",
            'Europe/Riga': "Riga",
            'Europe/Sofia': "Sofia",
            'Europe/Tallinn': "Tallinn",
            'Europe/Vilnius': "Vilnius",
            'Europe/Athens': "Athens",
            'Europe/Istanbul': "Istanbul",
            'Europe/Minsk': "Minsk",
            'Asia/Jerusalem': "Jerusalem",
            'Africa/Harare': "Harare",
            'Africa/Johannesburg': "Pretoria",
            'Europe/Kaliningrad': "Kaliningrad",
            'Europe/Moscow': "St. Petersburg",
            'Europe/Volgograd': "Volgograd",
            'Europe/Samara': "Samara",
            'Asia/Kuwait': "Kuwait",
            'Asia/Riyadh': "Riyadh",
            'Africa/Nairobi': "Nairobi",
            'Asia/Baghdad': "Baghdad",
            'Asia/Tehran': "Tehran",
            'Asia/Muscat': "Muscat",
            'Asia/Baku': "Baku",
            'Asia/Tbilisi': "Tbilisi",
            'Asia/Yerevan': "Yerevan",
            'Asia/Kabul': "Kabul",
            'Asia/Yekaterinburg': "Ekaterinburg",
            'Asia/Karachi': "Karachi",
            'Asia/Tashkent': "Tashkent",
            'Asia/Kolkata': "New Delhi",
            'Asia/Kathmandu': "Kathmandu",
            'Asia/Dhaka': "Dhaka",
            'Asia/Colombo': "Sri Jayawardenepura",
            'Asia/Almaty': "Almaty",
            'Asia/Novosibirsk': "Novosibirsk",
            'Asia/Rangoon': "Rangoon",
            'Asia/Bangkok': "Hanoi",
            'Asia/Jakarta': "Jakarta",
            'Asia/Krasnoyarsk': "Krasnoyarsk",
            'Asia/Shanghai': "Beijing",
            'Asia/Chongqing': "Chongqing",
            'Asia/Hong_Kong': "Hong Kong",
            'Asia/Urumqi': "Urumqi",
            'Asia/Kuala_Lumpur': "Kuala Lumpur",
            'Asia/Singapore': "Singapore",
            'Asia/Taipei': "Taipei",
            'Australia/Perth': "Perth",
            'Asia/Irkutsk': "Irkutsk",
            'Asia/Ulaanbaatar': "Ulaanbaatar",
            'Asia/Seoul': "Seoul",
            'Asia/Tokyo': "Tokyo",
            'Asia/Yakutsk': "Yakutsk",
            'Australia/Darwin': "Darwin",
            'Australia/Adelaide': "Adelaide",
            'Australia/Melbourne': "Melbourne",
            'Australia/Sydney': "Sydney",
            'Australia/Brisbane': "Brisbane",
            'Australia/Hobart': "Hobart",
            'Asia/Vladivostok': "Vladivostok",
            'Pacific/Guam': "Guam",
            'Pacific/Port_Moresby': "Port Moresby",
            'Asia/Magadan': "Magadan",
            'Asia/Srednekolymsk': "Srednekolymsk",
            'Pacific/Guadalcanal': "Solomon Is.",
            'Pacific/Noumea': "New Caledonia",
            'Pacific/Fiji': "Fiji",
            'Asia/Kamchatka': "Kamchatka",
            'Pacific/Majuro': "Marshall Is.",
            'Pacific/Auckland': "Wellington",
            'Pacific/Tongatapu': "Nuku'alofa",
            'Pacific/Fakaofo': "Tokelau Is.",
            'Pacific/Chatham': "Chatham Is.",
            'Pacific/Apia': "Samoa",
        };


        $rootScope.guessTimezone = function() {
            return moment.tz.guess(); // America/Chicago
        };

        // PWD @todo - change this to use https://momentjs.com/timezone/docs/#/using-timezones/guessing-user-timezone/
        // timezone will then be in the format 'America/Chicago' which is independent of if you are in DST or ST
        $rootScope.getLocalTimezone = function() {
            var timezoneOffset, timezone;

            try {
                // just in case there's an issue
                timezoneOffset = Math.round(new Date().getTimezoneOffset() / 60);
            } catch (e) {
                // Default to UTC
                timezoneOffset = 0;
            }

            if (timezoneOffset === 0) {
                timezone = 'Etc/UTC';
            } else if (timezoneOffset > 0) {
                timezone = 'Etc/GMT+' + timezoneOffset.toString(); // include the plus sign
            } else {
                timezone = 'Etc/GMT' + timezoneOffset.toString(); // number already has the minus sign
            }

            return timezone;
        };

        // Given one of the Etc values, return the offset, or UTC if no offset.
        $rootScope.getTzOffset = function(tz) {
            var offset = 'UTC';
            switch (tz) {
                case 'Etc/GMT+12':
                    offset = '-1200';
                    break;
                case 'Etc/GMT+11':
                    offset = '-1100';
                    break;
                case 'Etc/GMT+10':
                    offset = '-1000';
                    break;
                case 'Etc/GMT+9':
                    offset = '-0900';
                    break;
                case 'Etc/GMT+8':
                    offset = '-0800';
                    break;
                case 'Etc/GMT+7':
                    offset = '-0700';
                    break;
                case 'Etc/GMT+6':
                    offset = '-0600';
                    break;
                case 'Etc/GMT+5':
                    offset = '-0500';
                    break;
                case 'Etc/GMT+4':
                    offset = '-0400';
                    break;
                case 'Etc/GMT+3':
                    offset = '-0300';
                    break;
                case 'Etc/GMT+2':
                    offset = '-0200';
                    break;
                case 'Etc/GMT+1':
                    offset = '-0100';
                    break;
                case 'Etc/UTC':
                    offset = 'UTC';
                    break;
                case 'Etc/GMT-1':
                    offset = '+0100';
                    break;
                case 'Etc/GMT-2':
                    offset = '+0200';
                    break;
                case 'Etc/GMT-3':
                    offset = '+0300';
                    break;
                case 'Etc/GMT-4':
                    offset = '+0400';
                    break;
                case 'Etc/GMT-5':
                    offset = '+0500';
                    break;
                case 'Etc/GMT-6':
                    offset = '+0600';
                    break;
                case 'Etc/GMT-7':
                    offset = '+0700';
                    break;
                case 'Etc/GMT-8':
                    offset = '+0800';
                    break;
                case 'Etc/GMT-9':
                    offset = '+0900';
                    break;
                case 'Etc/GMT-10':
                    offset = '+1000';
                    break;
                case 'Etc/GMT-11':
                    offset = '+1100';
                    break;
                case 'Etc/GMT-12':
                    offset = '+1200';
                    break;
                case 'Etc/GMT-13':
                    offset = '+1300';
                    break;
            }

            return offset;
        };

        /* getPremiumPermissionForUser
         *      Loop over an array of features and return the one we need (based on "name")
         *
         * @param {premiumFeatures}     [REQUIRED] array    The array of features to loop over
         * @param {feature}             [REQUIRED] str      The "name" of the feature to look for
         * @param {key}                 [REQUIRED] str      The value on the feature to look for
         * @param {def}                 [REQUIRED] any      Default value to return if the requested feature/key is not found
         */
        $rootScope.getPremiumPermissionForUser = function(premiumFeatures, feature, key, def) {
            var retVal;
            var found = false;
            for( var p=0, pLen=premiumFeatures.length; p<pLen; p++ ) {
                if( premiumFeatures[p].name === feature ) {
                    if( premiumFeatures[p][key] !== undefined ) {
                        found = true;

                        if( premiumFeatures[p].enabled !== undefined && premiumFeatures[p].enabled ) {
                            // Feature/key exists and is enabled
                            if( key === 'count' ) {
                                // -1 means infinity
                                retVal = (premiumFeatures[p].count === -1) ? Infinity : premiumFeatures[p].count;
                            } else {
                                // Just pass the field along
                                retVal = premiumFeatures[p][key];
                            }
                        } else {
                            // Feature exists but is disabled, so return default value
                            retVal = def;
                            // $log.log("getPremiumPermissionForUser called. Found '" + feature + "', '" + key + "' in the feature set, but disabled. Returning default value '" + def + "' instead.");
                        }
                    }
                    
                    // We found the feature, even if not the key, so nothing more to look for
                    break;
                }
            }

            if( !found ) {
                retVal = def;
                $log.error("getPremiumPermissionForUser called. Unable to find '" + feature + "', '" + key + "' in the feature set. Returning default value '" + def + "' instead. premiumFeatures data:", premiumFeatures);
            }

            return retVal;
        };
    }]);
});
